/**
 * AlternativesStrip — renders the 2-4 "Other options to consider" cards
 * that appear under the main TripSummaryCard when the AI returns an
 * `alternatives` array (vague prompts only — see backend prompt rule #5).
 *
 * Each card is compact and tappable: tapping sends a new chat message
 * "Plan this trip: <destination> for <N> nights" back through the chat
 * so the AI generates a full plan for that option.
 */
import React from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import PriceTag from '../pricing/PriceTag';

const TIER_BADGE = {
  budget: { label: '€', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  standard: { label: '€€', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  luxury: { label: '€€€', color: 'bg-blue-50 text-blue-800 border-blue-200' },
};

export default function AlternativesStrip({ alternatives, onPick }) {
  const { t } = useLanguage();
  if (!Array.isArray(alternatives) || alternatives.length === 0) return null;

  return (
    <div className="mt-4 mb-2" data-testid="alternatives-strip">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-blue-500" />
        <h4 className="text-sm font-bold text-gray-900">{t('chat.alternativesTitle')}</h4>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {alternatives.map((alt, idx) => {
          const tierStyle = TIER_BADGE[alt.tier] || TIER_BADGE.standard;
          return (
            <button
              type="button"
              key={`${alt.destinationEn || alt.destination}-${idx}`}
              data-testid={`alternative-card-${idx}`}
              onClick={() => onPick && onPick(alt)}
              className="text-left bg-white border border-gray-200 hover:border-blue-400 hover:shadow-md rounded-xl p-3 transition-all group"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="font-bold text-gray-900 text-sm truncate">{alt.destination}</div>
                  {alt.durationNights ? (
                    <div className="text-[11px] text-gray-500">
                      {alt.durationNights} {t('common.nights')}
                    </div>
                  ) : null}
                </div>
                <span className={`flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded border font-semibold ${tierStyle.color}`}>
                  {tierStyle.label}
                </span>
              </div>

              <p className="text-xs text-gray-600 leading-snug mb-2 line-clamp-2" data-testid={`alternative-why-${idx}`}>
                {alt.whyItWorks}
              </p>

              <div className="flex items-end justify-between gap-2 pt-2 border-t border-gray-100">
                <PriceTag
                  price={alt.totalEstimate}
                  tier="estimated"
                  size="sm"
                  showBadge={false}
                  testId={`alternative-price-${idx}`}
                />
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 group-hover:text-blue-700">
                  {t('chat.alternativesPlan')}
                  <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
